import { useQuery } from '@tanstack/react-query';

import { CustomerStatisticsMessage } from '../../../types/StatisticsMessage';
import {
    getPortReports,
    getProductReports,
    getSupplierReports,
    getVesselReports,
} from '../../common/api/clients/gateway.api';
import { getGeneralReport, getProductGroupReport } from '../../common/api/clients/report.api';
import { queries } from '../../common/api/queryKeys/queries';
import { mapProductGroupsReports } from '../utils/mapProductGroupsReports';
import { sortReportsByName } from '../utils/sortReports';

interface FetchCustomerStatisticsParams {
    from: number;
    to: number;
    customerIds?: string[];
}

export const useCustomerStatistics = (params: FetchCustomerStatisticsParams) => {
    const fetchCustomerStatistics = async (
        params: FetchCustomerStatisticsParams
    ): Promise<CustomerStatisticsMessage> => {
        const [general, vessel, supplier, product, port, productGroup] = await Promise.all([
            getGeneralReport(params),
            getVesselReports(params),
            getSupplierReports(params),
            getProductReports(params),
            getPortReports(params),
            getProductGroupReport(params),
        ]);

        const mappedVesselReports = Object.values(vessel.reports);
        const mappedSupplierReports = Object.values(supplier.reports);
        const mappedPortReports = Object.values(port.reports);
        const mappedProductReports = Object.values(product.reports);

        const mappedAndSortedProductGroupReports = mapProductGroupsReports(productGroup.reports).sort((a, b) =>
            a.productGroup.localeCompare(b.productGroup)
        );

        return {
            period: general.period,
            general: general.item,
            vessel: mappedVesselReports.sort(sortReportsByName),
            supplier: mappedSupplierReports.sort(sortReportsByName),
            port: mappedPortReports.sort(sortReportsByName),
            product: mappedProductReports.sort(sortReportsByName),
            productGroup: mappedAndSortedProductGroupReports,
        };
    };

    const fiveMinutesInMilliseconds = 5 * 60 * 1000;

    return useQuery({
        queryKey: queries.reports.customer(params).queryKey,
        queryFn: () => fetchCustomerStatistics(params),
        staleTime: fiveMinutesInMilliseconds,
    });
};
